import { Component, lazy, Suspense, useState, useCallback, useEffect } from 'react';
import {subscribeNavigation} from './shared/navigation/subscribeNavigation.js';
import { LEGACY_PAGES } from './modules/dashboard/legacy/registry.js';
import DashboardPage from './modules/dashboard/DashboardPage.jsx';
import { AuthProvider, useAuth } from './shared/auth/AuthContext.jsx';
import OrbitAIProvider from './shared/orbit-ai/OrbitAIProvider.jsx';
import { WorkspaceProvider } from './shared/workspace/WorkspaceContext.jsx';
import LoginPage from './shared/auth/LoginPage.jsx';
import OrbitModuleHeader from './shared/hub/OrbitModuleHeader.jsx';
import HubPage from './shared/hub/HubPage.jsx';
const UsersPage = lazy(() => import('./modules/user-management/UsersPage.jsx'));
const ClientManagementApp = lazy(() => import('./modules/client-management/ClientManagementApp.jsx'));
const OrbitDevelopmentApp = lazy(() => import('./modules/orbit-development/OrbitDevelopmentApp.jsx'));
const SqlEditorPage = lazy(() => import('./modules/sql-query-tool/SqlEditorPage.jsx'));
const ProjectManagementApp = lazy(
  () => import('./modules/project-management/ProjectManagementApp.jsx'));
const TaxonomyApp = lazy(() => import('./modules/taxonomy-generator/TaxonomyApp.jsx'));
const ReachToolApp = lazy(() => import('./modules/reach-tool/ReachToolApp.jsx'));
const PaidMediaToolApp = lazy(() => import('./modules/paid-media/PaidMediaToolApp.jsx'));
const CompetitiveMonitoringApp = lazy(() => import('./modules/competitive-monitoring/CompetitiveMonitoringApp.jsx'));
import { MODULES, moduleForPath } from './shared/modules/registry.js';

const SettingsApp = lazy(() => import('./modules/settings/SettingsApp.jsx'));
const DashboardDevelopmentApp = lazy(() => import('./modules/dashboard-development/DashboardDevelopmentApp.jsx'));

const MODULE = 'dashboard';

const DASHBOARD_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'paid-media', label: 'Paid Media' },
  { id: 'crm', label: 'CRM' },
  { id: 'wa-acquisition', label: 'Website Analytics' },
  { id: 'social-organic', label: 'Social Organic' },
  { id: 'social-listening', label: 'Social Listening' },
];

// ─── routing helpers ──────────────────────────────────────────────────────────

function getPath() {
  const path = window.location.pathname;
  // Preserve existing bookmarks while all generated links use the canonical route.
  const canonical = path.replace(/^\/dashboard-demo(?=\/|$)/, '/dashboard');
  const destination = canonical === '/dashboard' || canonical === '/dashboard/'
    ? '/dashboard/overview' : canonical;
  if (destination !== path) {
    history.replaceState(null, '', destination + window.location.search + window.location.hash);
  }
  return destination;
}

function pageIdFromPath(path) {
  // /dashboard/<id>  →  id
  const prefix = '/' + MODULE + '/';
  if (!path.startsWith(prefix)) return null;
  const id = path.slice(prefix.length);
  return LEGACY_PAGES.some(p => p.id === id) ? id : null;
}

// ─── inner router ────────────────────────────────────────────────────────────

function Router() {
  const { user, loading } = useAuth();
  const [path, setPath] = useState(getPath);
  // A local-only visual review surface for the Paid Media module. `import.meta.env.DEV`
  // is false in production builds, so this never creates an unauthenticated product route.
  const paidMediaPreview = import.meta.env.DEV && path === '/paid-media-preview';

  useEffect(() => {
    return subscribeNavigation(() => setPath(getPath()));
  }, []);

  // The browser tab names where you are: the module you are inside, or Orbit on the Hub and
  // on login. Set here rather than in each module because routing is hand-rolled — there is no
  // router to hang it off — and because the registry already answers "which module owns this
  // path" for the Hub and the backend. A module that renders its own header therefore needs
  // no title code of its own, and one that doesn't still gets a correct tab.
  useEffect(() => {
    // moduleForPath matches a module's registered entry path, which is exact for every module
    // but one: Dashboard registers /dashboard/overview, so its other legacy pages
    // would fall through to "Orbit" while sitting squarely inside the tool. Falling back to the
    // first path segment covers that without naming it — and still leaves /login and the Hub,
    // which match no module, as Orbit.
    const seg = p => '/' + ((p || '').split('/')[1] || '');
    const mod = moduleForPath(path)
      || MODULES.find(m => m.path && seg(m.path) === seg(path));
    document.title = mod ? mod.label : 'Orbit';
  }, [path]);

  const navigateDashboard = useCallback((id) => {
    history.pushState(null, '', '/' + MODULE + '/' + id);
    window.scrollTo(0, 0);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  // Intercept data-nav clicks inside legacy pages → /dashboard/<id>
  const onClickCapture = useCallback((e) => {
    const link = e.target.closest('[data-nav]');
    if (!link) return;
    const id = link.getAttribute('data-nav');
    if (LEGACY_PAGES.some(p => p.id === id)) {
      e.preventDefault();
      navigateDashboard(id);
    }
  }, [navigateDashboard]);

  if (loading) return <Spinner />;

  if (paidMediaPreview) {
    return <Suspense fallback={<Spinner />}><PaidMediaToolApp preview /></Suspense>;
  }

  const AUTH_ENABLED = true;

  // ── Public route: login ───────────────────────────────────────────────────
  if (path === '/orbit-ai' && user) return <HubPage />;

  if (path === '/login') {
    if (!AUTH_ENABLED || user) {
      history.replaceState(null, '', '/');
      setPath('/');
      return null;
    }
    return <LoginPage />;
  }

  // ── Auth guard ────────────────────────────────────────────────────────────
  if (AUTH_ENABLED && !user) {
    const dest = `/login${path !== '/' ? '?redirect=' + encodeURIComponent(path) : ''}`;
    history.replaceState(null, '', dest);
    setPath('/login');
    return <LoginPage />;
  }

  // ── Hub ───────────────────────────────────────────────────────────────────
  if (path === '/' || path === '') {
    return <HubPage />;
  }

  // ── Users management ──────────────────────────────────────────────────────
  if (path === '/settings' || path.startsWith('/settings/')) {
    return <Suspense fallback={<Spinner />}><SettingsApp /></Suspense>;
  }

  if (path === '/users') {
    return <Suspense fallback={<Spinner />}><UsersPage /></Suspense>;
  }

  // ── Client Management ─────────────────────────────────────────────────────
  if (path.startsWith('/client-management')) {
    return <Suspense fallback={<Spinner />}><ClientManagementApp /></Suspense>;
  }

  // ── Orbit Development ─────────────────────────────────────────────────────
  if (path.startsWith('/orbit-development')) {
    return <Suspense fallback={<Spinner />}><OrbitDevelopmentApp /></Suspense>;
  }

  // ── SQL Editor ────────────────────────────────────────────────────────────
  if (path === '/sql-editor' || path.startsWith('/sql-editor/')) {
    return <Suspense fallback={<Spinner />}><SqlEditorPage /></Suspense>;
  }

  // ── Project Management ────────────────────────────────────────────────────
  if (path.startsWith('/project-management')) {
    return <Suspense fallback={<Spinner />}><ProjectManagementApp /></Suspense>;
  }

  // ── Taxonomy Generator ────────────────────────────────────────────────────
  if (path.startsWith('/taxonomy-generator')) {
    return <Suspense fallback={<Spinner />}><TaxonomyApp /></Suspense>;
  }

  // ── Reach Tool ─────────────────────────────────────────────────────────────
  if (path.startsWith('/reach-tool')) {
    return <Suspense fallback={<Spinner />}><ReachToolApp /></Suspense>;
  }

  // ── Paid Media Tool ──────────────────────────────────────────────────────
  if (path.startsWith('/paid-media')) {
    return <Suspense fallback={<Spinner />}><PaidMediaToolApp /></Suspense>;
  }

  // ── Competitive Monitoring ─────────────────────────────────────────────────
  if (path.startsWith('/competitive-monitoring')) {
    return <Suspense fallback={<Spinner />}><CompetitiveMonitoringApp /></Suspense>;
  }

  if (path === '/dashboard-development' || path.startsWith('/dashboard-development/')) {
    return <Suspense fallback={<Spinner />}><DashboardDevelopmentApp /></Suspense>;
  }

  // ── Dashboard pages ──────────────────────────────────────────────────
  const pageId = pageIdFromPath(path);
  if (pageId) {
    const page = LEGACY_PAGES.find(p => p.id === pageId);
    return (
      <div className="o-legacy-shell" onClickCapture={onClickCapture}>
        <OrbitModuleHeader
          title="Dashboard"
          moduleId="dashboard"
          tabs={DASHBOARD_TABS}
          activeTab={pageId.startsWith('wa-') ? 'wa-acquisition' : pageId}
          onTabChange={navigateDashboard}
        />
        <DashboardPage page={page} />
      </div>
    );
  }

  // ── Unknown path → hub ────────────────────────────────────────────────────
  history.replaceState(null, '', '/');
  setPath('/');
  return <HubPage />;
}

function Spinner() {
  return (
    <div className="o-state" style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      Loading…
    </div>
  );
}

class ScreenErrorBoundary extends Component {
  state = {failed:false};
  static getDerivedStateFromError() { return {failed:true}; }
  componentDidCatch(error) { console.error('Unable to load screen', error); }
  render() {
    return this.state.failed ? <main className="o-page"><div className="o-state o-error" role="alert">
      <p>This screen could not be loaded. Reload to get the latest version and try again.</p>
      <button className="o-btn" onClick={() => window.location.reload()}>Reload</button>
    </div></main> : this.props.children;
  }
}

export default function App() {
  // Providers capture the initial URL too; resolve old bookmarks before they mount.
  getPath();
  return (
    <AuthProvider>
      {/* Inside AuthProvider because it needs the token; outside Router so the selected
          workspace survives navigation. */}
      <WorkspaceProvider>
        <OrbitAIProvider><ScreenErrorBoundary><Router /></ScreenErrorBoundary></OrbitAIProvider>
      </WorkspaceProvider>
    </AuthProvider>
  );
}
